import React, { useState } from 'react';
import { DownOutline } from 'antd-mobile-icons';

import type { ClientJson } from '../../../types/models';

import { GoogleMapsIcon, WazeIcon } from './brand-logos';

const DAY_LABELS: Record<string, string> = {
  mon: 'Lun',
  tue: 'Mar',
  wed: 'Mer',
  thu: 'Jeu',
  fri: 'Ven',
};
const LICENSE_LABELS: Record<string, string> = {
  vl: 'Permis VL',
  pl: 'Permis PL',
  spl: 'Permis SPL',
  none: 'Sans permis',
};
const VEHICLE_LABELS: Record<string, string> = {
  vl: 'VL',
  vul: 'VUL',
  pl: 'PL',
  tracteur: 'Tracteur',
  semi: 'Semi',
};
const WASH_LABELS: Record<string, string> = {
  exterieur: 'Extérieur',
  interieur_caisse: 'Intérieur caisse',
  desinfection_caisse: 'Désinfection caisse',
  interieur_cabine: 'Intérieur cabine',
  autre: 'Autre',
};
const FREQ_LABELS: Record<string, string> = {
  quotidien: 'Quotidien',
  hebdo: 'Hebdo',
  quinzo: 'Quinzo',
  mensuel: 'Mensuel',
  autre: 'Autre',
};

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      background: 'rgba(49,220,200,0.14)',
      border: '1px solid rgba(49,220,200,0.28)',
      borderRadius: 100,
      color: 'var(--teal)',
      fontSize: 12,
      fontWeight: 600,
      padding: '3px 10px',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const NavLink: React.FC<{ href: string; children: React.ReactNode }> = ({ children, href }) => (
  <a
    href={href}
    rel="noopener noreferrer"
    target="_blank"
    style={{
      alignItems: 'center',
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: 8,
      color: '#0B1628',
      display: 'inline-flex',
      fontSize: 12.5,
      fontWeight: 600,
      gap: 6,
      padding: '6px 12px',
      textDecoration: 'none',
    }}
  >
    {children}
  </a>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ children, label }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <div
      style={{
        color: 'var(--text-muted)',
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>{children}</div>
  </div>
);

export const ExploitationCard: React.FC<{ client: ClientJson }> = ({ client }) => {
  const [open, setOpen] = useState(false);
  const days = client.intervention_days ?? [];
  const hours = client.intervention_hours ?? {};
  const licenses = client.required_licenses ?? [];
  const vehicles = client.vehicle_types ?? [];
  const freqs = client.vehicle_wash_frequencies ?? {};
  const washTypes = client.wash_types ?? [];
  const address = client.intervention_address ?? '';
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;

  const hasSchedule = days.length > 0;
  const hasAnything =
    hasSchedule ||
    licenses.length > 0 ||
    vehicles.length > 0 ||
    washTypes.length > 0 ||
    !!client.intervention_address ||
    !!client.intervention_name ||
    !!client.intervention_phone;

  if (!hasAnything) {
    return null;
  }

  const formatHours = (key: string): string | null => {
    const h = hours[key];
    if (!h || (!h.start && !h.end)) {
      return null;
    }
    return `${h.start ?? '—'}–${h.end ?? '—'}`;
  };

  const freqLabel = (vehicle: string): string | null => {
    const freq = freqs[vehicle];
    if (freq === 'autre' && client.wash_frequency_other) {
      return client.wash_frequency_other;
    }
    return FREQ_LABELS[freq] ?? null;
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(49,220,200,0.10), rgba(49,220,200,0.03))',
        border: '1px solid rgba(49,220,200,0.28)',
        borderLeft: '4px solid var(--teal)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: open ? 14 : 0,
        margin: '10px 8px 12px',
        padding: '14px',
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        role="button"
        style={{
          alignItems: 'center',
          color: 'var(--teal)',
          cursor: 'pointer',
          display: 'flex',
          fontSize: 12.5,
          fontWeight: 800,
          justifyContent: 'space-between',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        <span>🛠️ Infos exploitation</span>
        <DownOutline
          style={{ fontSize: 14, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
        />
      </div>

      {open && client.intervention_address && (
        <Row label="Adresse du site">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <span style={{ color: 'var(--text-primary)', fontSize: 13.5, lineHeight: 1.4 }}>
              {client.intervention_address}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <NavLink href={mapsUrl}>
                <GoogleMapsIcon size={15} />
                Google Maps
              </NavLink>
              <NavLink href={wazeUrl}>
                <WazeIcon size={15} />
                Waze
              </NavLink>
            </div>
          </div>
        </Row>
      )}

      {open && (client.intervention_name || client.intervention_phone) && (
        <Row label="Contact">
          <span style={{ color: 'var(--text-primary)', fontSize: 13.5 }}>
            {[client.intervention_name, client.intervention_phone].filter(Boolean).join(' · ')}
          </span>
        </Row>
      )}

      {open && hasSchedule && (
        <Row label="Jours & horaires">
          {days.map((d) => {
            const range = formatHours(d);
            return (
              <Chip key={d}>
                {DAY_LABELS[d] ?? d}
                {range ? ` ${range}` : ''}
              </Chip>
            );
          })}
        </Row>
      )}

      {open && licenses.length > 0 && (
        <Row label="Permis requis">
          {licenses.map((l) => (
            <Chip key={l}>{LICENSE_LABELS[l] ?? l}</Chip>
          ))}
        </Row>
      )}

      {open && vehicles.length > 0 && (
        <Row label="Véhicules & fréquence">
          {vehicles.map((v) => {
            const freq = freqLabel(v);
            return (
              <Chip key={v}>
                {VEHICLE_LABELS[v] ?? v}
                {freq ? ` · ${freq}` : ''}
              </Chip>
            );
          })}
        </Row>
      )}

      {open && washTypes.length > 0 && (
        <Row label="Lavages demandés">
          {washTypes.map((w) => {
            const label = w === 'autre' && client.wash_type_other ? client.wash_type_other : WASH_LABELS[w] ?? w;
            return <Chip key={w}>{label}</Chip>;
          })}
        </Row>
      )}
    </div>
  );
};
